import React, { useEffect, useState } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Box,
    Typography,
    IconButton,
    Button,
    Chip,
    useTheme,
    useMediaQuery,
} from "@mui/material";
import DOMPurify from "dompurify";
import CloseIcon from "@mui/icons-material/Close";
import ShoppingBagIcon from "@mui/icons-material/ShoppingBag";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import ImageIcon from "@mui/icons-material/Image";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import RatingStars from "./RatingStars";
import { usdFmt } from "../utils/dataUtils";

export default function QuickView({ product, onClose }) {
    const [index, setIndex] = useState(0);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const isSmall = useMediaQuery(theme.breakpoints.down('sm'));

    useEffect(() => setIndex(0), [product]);
    if (!product) return null;

    const images = product.images && product.images.length ? product.images : [];
    const prev = () => setIndex(i => (i - 1 + images.length) % images.length);
    const next = () => setIndex(i => (i + 1) % images.length);

    return (
        <Dialog
            open={!!product}
            onClose={onClose}
            maxWidth={isMobile ? "sm" : "lg"}
            fullWidth
            fullScreen={isSmall}
            sx={{
                '& .MuiDialog-paper': {
                    margin: isMobile ? 1 : 3,
                    maxHeight: isMobile ? 'calc(100vh - 16px)' : 'calc(100vh - 64px)',
                }
            }}
        >
            <DialogTitle sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                pb: 1,
                pr: 1
            }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="caption" color="primary" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                        {product.brand}
                    </Typography>
                    <Typography
                        variant="h6"
                        sx={{
                            fontSize: { xs: '1rem', sm: '1.25rem' },
                            fontWeight: 500,
                            display: '-webkit-box',
                            WebkitLineClamp: { xs: 1, sm: 2 },
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden'
                        }}
                    >
                        {product.name}
                    </Typography>
                </Box>
                <IconButton onClick={onClose} sx={{ ml: 1 }}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent dividers sx={{ p: { xs: 1, sm: 2 } }}>
                <Box sx={{
                    display: 'flex',
                    gap: { xs: 1, sm: 2 },
                    flexDirection: { xs: 'column', md: 'row' }
                }}>
                    {/* Image Gallery */}
                    <Box sx={{
                        flex: { xs: '1', md: '0 0 520px' },
                        width: { xs: '100%', md: 520 },
                        position: 'relative',
                        bgcolor: 'grey.100',
                        borderRadius: 1,
                        overflow: 'hidden'
                    }}>
                        <Box sx={{
                            width: '100%',
                            pt: { xs: '75%', md: '62.5%' },
                            position: 'relative',
                            overflow: 'hidden'
                        }}>
                            {images[index] ? (
                                <img
                                    src={images[index]}
                                    alt={`img-${index}`}
                                    style={{
                                        position: 'absolute',
                                        inset: 0,
                                        width: '100%',
                                        height: '100%',
                                        objectFit: 'contain',
                                        background: '#fff'
                                    }}
                                    onError={(e) => { e.currentTarget.src = 'https://placehold.co/800x600?text=Image'; }}
                                />
                            ) : (
                                <Box sx={{
                                    position: 'absolute',
                                    inset: 0,
                                    display: 'grid',
                                    placeItems: 'center'
                                }}>
                                    <ImageIcon sx={{ fontSize: { xs: 48, sm: 64 }, color: 'grey.400' }} />
                                </Box>
                            )}
                        </Box>

                        {images.length > 1 && (
                            <>
                                <IconButton
                                    onClick={prev}
                                    sx={{
                                        position: 'absolute',
                                        left: { xs: 4, sm: 8 },
                                        top: '50%',
                                        transform: 'translateY(-50%)',
                                        bgcolor: 'rgba(255,255,255,0.8)',
                                        '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' },
                                        zIndex: 1
                                    }}
                                >
                                    <ArrowBackIosNewIcon />
                                </IconButton>
                                <IconButton
                                    onClick={next}
                                    sx={{
                                        position: 'absolute',
                                        right: { xs: 4, sm: 8 },
                                        top: '50%',
                                        transform: 'translateY(-50%)',
                                        bgcolor: 'rgba(255,255,255,0.8)',
                                        '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' },
                                        zIndex: 1
                                    }}
                                >
                                    <ArrowForwardIosIcon />
                                </IconButton>

                                {/* Thumbnails - hidden on mobile to save space */}
                                <Box sx={{
                                    mt: 1,
                                    display: { xs: 'none', sm: 'flex' },
                                    gap: 1,
                                    overflowX: 'auto',
                                    py: 1,
                                    px: 1
                                }}>
                                    {images.map((src, i) => (
                                        <Box
                                            key={src + i}
                                            sx={{
                                                width: 88,
                                                height: 60,
                                                flex: '0 0 auto',
                                                border: i === index ? '2px solid' : '1px solid',
                                                borderColor: i === index ? 'primary.main' : 'divider',
                                                borderRadius: 1,
                                                overflow: 'hidden',
                                                cursor: 'pointer',
                                                transition: 'all 0.2s ease',
                                                '&:hover': {
                                                    borderColor: 'primary.main',
                                                    opacity: 0.8
                                                }
                                            }}
                                            onClick={() => setIndex(i)}
                                        >
                                            <img
                                                src={src}
                                                alt={`thumb-${i}`}
                                                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                                onError={(e) => { e.currentTarget.style.display = 'none'; }}
                                            />
                                        </Box>
                                    ))}
                                </Box>

                                {/* Mobile indicators */}
                                <Box sx={{
                                    display: { xs: 'flex', sm: 'none' },
                                    justifyContent: 'center',
                                    gap: 0.5,
                                    position: 'absolute',
                                    bottom: 8,
                                    left: '50%',
                                    transform: 'translateX(-50%)',
                                    zIndex: 1
                                }}>
                                    {images.map((_, i) => (
                                        <Box
                                            key={i}
                                            sx={{
                                                width: 8,
                                                height: 8,
                                                borderRadius: '50%',
                                                bgcolor: i === index ? 'primary.main' : 'rgba(255,255,255,0.6)',
                                                cursor: 'pointer',
                                                transition: 'all 0.2s ease'
                                            }}
                                            onClick={() => setIndex(i)}
                                        />
                                    ))}
                                </Box>
                            </>
                        )}
                    </Box>

                    {/* Product Details */}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box sx={{ mb: 2 }}>
                            <RatingStars value={product.rating} />
                            <Box sx={{ mt: 1 }}>
                                {product.priceUsd != null ? (
                                    <Typography variant="h4" sx={{ fontSize: { xs: '1.5rem', sm: '2rem' }, fontWeight: 600 }}>
                                        {usdFmt.format(product.priceUsd)}
                                    </Typography>
                                ) : (
                                    <Typography variant="h5" color="text.secondary" sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
                                        Price on request
                                    </Typography>
                                )}
                                {product.compareAtUsd && product.compareAtUsd > product.priceUsd && (
                                    <Typography
                                        variant="body1"
                                        color="text.secondary"
                                        sx={{
                                            textDecoration: 'line-through',
                                            fontSize: { xs: '0.875rem', sm: '1rem' }
                                        }}
                                    >
                                        {usdFmt.format(product.compareAtUsd)}
                                    </Typography>
                                )}
                            </Box>
                        </Box>

                        {/* Description */}
                        <Box
                            sx={{
                                mb: 2,
                                maxHeight: { xs: 120, sm: 200 },
                                overflowY: 'auto',
                                '& p': { fontSize: { xs: '0.875rem', sm: '1rem' } },
                                '& ul, & ol': { fontSize: { xs: '0.875rem', sm: '1rem' } }
                            }}
                            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(product.descriptionHtml || "") }}
                        />

                        {/* Tags */}
                        <Box sx={{ mb: 3, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            {product.tags.slice(0, isMobile ? 6 : product.tags.length).map(t =>
                                <Chip
                                    key={t}
                                    label={t}
                                    size="small"
                                    sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                                />
                            )}
                        </Box>

                        {/* Action Buttons - Improved for tablets and mobile */}
                        <Box sx={{
                            display: 'flex',
                            gap: { xs: 1, sm: 2 },
                            flexDirection: { xs: 'column', sm: 'row' },
                            '& .MuiButton-root': {
                                fontSize: { xs: '0.875rem', sm: '1rem' },
                                py: { xs: 1, sm: 1.5 }
                            }
                        }}>
                            <Button
                                variant="contained"
                                startIcon={<ShoppingBagIcon />}
                                href={`mailto:info@skinnthrive.com?subject=Inquiry%20-%20${encodeURIComponent(product.name)}&body=Hi%20Team%2C%0A%0AI'd%20like%20to%20order%20this%20product%3A%0A- Product: ${encodeURIComponent(product.name)}%0A- SKU: ${encodeURIComponent(product.sku || "N/A")}%0A- Brand: ${encodeURIComponent(product.brand)}%0A`}
                                sx={{ flex: { xs: 1, sm: 'none' } }}
                            >
                                Inquiry by Email
                            </Button>
                            <Button
                                variant="outlined"
                                startIcon={<WhatsAppIcon />}
                                href="https://wa.me/919586454269" // WhatsApp link format
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{ flex: { xs: 1, sm: 'none' } }}
                            >
                                WhatsApp
                            </Button>

                        </Box>

                        {/* Product Info */}
                        <Box sx={{
                            mt: 3,
                            pt: 2,
                            borderTop: 1,
                            borderColor: 'divider',
                            display: { xs: 'none', sm: 'block' }
                        }}>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                <strong>Category:</strong> {product.category}
                            </Typography>
                            {product.sku && (
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                    <strong>SKU:</strong> {product.sku}
                                </Typography>
                            )}
                            <Typography variant="body2" color="text.secondary">
                                <strong>Brand:</strong> {product.brand}
                            </Typography>
                        </Box>
                    </Box>
                </Box>
            </DialogContent>

            {isMobile && (
                <DialogActions sx={{ p: 2, pt: 1 }}>
                    <Button onClick={onClose} fullWidth variant="outlined">
                        Close
                    </Button>
                </DialogActions>
            )}
        </Dialog>
    );
}
